import type { ReactNode } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Icon, type IconName } from './Icon';

export type NoticeTone = 'info' | 'warning' | 'success' | 'error';

const TONE: Record<NoticeTone, { bg: string; icon: IconName; iconColor: string }> = {
	info: { bg: 'info.subtle', icon: 'info', iconColor: 'info.fg' },
	warning: { bg: 'warning.subtle', icon: 'alert', iconColor: 'warning.fg' },
	success: { bg: 'success.subtle', icon: 'checkCircle', iconColor: 'success.fg' },
	error: { bg: 'error.subtle', icon: 'xCircle', iconColor: 'error.fg' },
};

export interface NoticeProps {
	tone?: NoticeTone;
	title?: string;
	children?: ReactNode;
	action?: ReactNode;
}

/**
 * Inline status strip — title, body, optional action. Color comes from
 * semantic status tokens; the glyph is the matching Yaxle icon.
 */
export const Notice = ({ tone = 'info', title, children, action }: NoticeProps) => {
	const t = TONE[tone];
	return (
		<Flex
			align="flex-start"
			gap={3}
			px={4}
			py={3}
			borderRadius="md"
			bg={t.bg}
			borderWidth="1px"
			borderColor="border.default"
		>
			<Icon name={t.icon} color={t.iconColor} size="sm" mt="2px" />
			<Box flex={1} minW={0}>
				{title && (
					<Text color="fg.default" fontWeight="medium" fontSize="sm">
						{title}
					</Text>
				)}
				{children && (
					<Text color="fg.muted" fontSize="sm" mt={title ? 0.5 : 0}>
						{children}
					</Text>
				)}
			</Box>
			{action}
		</Flex>
	);
};

export default Notice;
